/**
 * Pure wall-drag safety helpers (no React/catalog).
 * Used by Line + automated bug suite.
 */

export function getLineOrientation(x1, y1, x2, y2) {
  const deltaX = Math.abs(x2 - x1);
  const deltaY = Math.abs(y2 - y1);
  if (deltaX > deltaY) return "horizontal";
  if (deltaY > deltaX) return "vertical";
  return "diagonal";
}

/**
 * Count angled (non-ortho) walls on a layer.
 * layerLike: { lines: Map|Object, vertices: Map|Object } with .get / .forEach
 */
export function countDiagonalWallsOnLayer(layer) {
  if (!layer) return 0;
  let n = 0;
  const lines = layer.get ? layer.get("lines") : layer.lines;
  const vertices = layer.get ? layer.get("vertices") : layer.vertices;
  if (!lines || !vertices) return 0;
  lines.forEach((line) => {
    const v0id = line.vertices.get
      ? line.vertices.get(0)
      : line.vertices[0];
    const v1id = line.vertices.get
      ? line.vertices.get(1)
      : line.vertices[1];
    const v0 = vertices.get ? vertices.get(v0id) : vertices[v0id];
    const v1 = vertices.get ? vertices.get(v1id) : vertices[v1id];
    if (!v0 || !v1) return;
    const dx = Math.abs(v0.x - v1.x);
    const dy = Math.abs(v0.y - v1.y);
    if (dx > 2 && dy > 2) n += 1;
  });
  return n;
}

/**
 * True if placing a wall at (x0,y0)-(x1,y1) would sit on another parallel wall.
 * Prevents Snap LN from hiding the opposite "top wall".
 *
 * lines: iterable of { id, v0:{x,y}, v1:{x,y}, isBridge?:boolean }
 * excludeId: dragged line id
 */
export function wouldCoincideWithParallelWalls(
  lines,
  excludeId,
  x0,
  y0,
  x1,
  y1
) {
  const orient = getLineOrientation(x0, y0, x1, y1);
  if (orient === "diagonal") return false;
  const tol = 5;
  for (let i = 0; i < lines.length; i++) {
    const other = lines[i];
    if (!other || other.id === excludeId || other.isBridge) continue;
    const a = other.v0;
    const b = other.v1;
    if (!a || !b) continue;
    const oOrient = getLineOrientation(a.x, a.y, b.x, b.y);
    if (oOrient !== orient) continue;
    if (orient === "horizontal") {
      if (Math.abs((a.y + b.y) / 2 - (y0 + y1) / 2) > tol) continue;
      const dMin = Math.min(x0, x1);
      const dMax = Math.max(x0, x1);
      const oMin = Math.min(a.x, b.x);
      const oMax = Math.max(a.x, b.x);
      if (dMax >= oMin - tol && oMax >= dMin - tol) return true;
    } else {
      if (Math.abs((a.x + b.x) / 2 - (x0 + x1) / 2) > tol) continue;
      const dMin = Math.min(y0, y1);
      const dMax = Math.max(y0, y1);
      const oMin = Math.min(a.y, b.y);
      const oMax = Math.max(a.y, b.y);
      if (dMax >= oMin - tol && oMax >= dMin - tol) return true;
    }
  }
  return false;
}

/** Click-only threshold used in endDraggingLine */
export const CLICK_MOVE_THRESHOLD_PX = 5;
